import { Button, FileUploader, Form, Popup, SelectBox, TextBox } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  EmptyItem,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import React, { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { postApi, putApi } from '../../apiUtils';
import { useTranslation } from '../../contexts/translation';
import { AttachmentCategory } from '../../types/attachmentCategory.types';
import {
  MaintenanceCost,
  MaintenanceCostInputs,
  MaintenanceCostType,
} from '../../types/maintenanceCost.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { dateFieldOptionsWithSerialization } from '../../utils/dates.types';
import { MAX_FILE_SIZE } from '../fileManager/AttachmentEditModal';
import { TabFilesDatagrid } from '../fileManager/TabFilesDatagrid';
import { uploadAttachment } from '../fileManager/uploadAttachment';
import './MaintenanceCostModal.scss';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  /** volá se po každém úspěšném zápisu, aby se přenačetl grid pod modálem */
  onSaved: () => void;
  assetId: number;
  costTypes?: MaintenanceCostType[];
  data?: MaintenanceCost | null;
  currency?: string;
  isReadOnly?: boolean;
}

/**
 * Přidání a editace nákladu na údržbu (#737).
 *
 * Přílohy jdou vybrat rovnou při zakládání. Drží se ve frontě i s atributy (název, popis,
 * kategorie) a nahrají se až po uložení - nejdřív se založí náklad, z odpovědi se vezme id
 * a na to se v cyklu pošle jeden upload na soubor. Modál pak zůstává otevřený a překlopí se
 * do režimu editace, kde je grid příloh. Protože už záznam existuje, druhé tlačítko je
 * "Zavřít", ne "Zrušit".
 */
/** Příloha čekající ve frontě na nahrání - soubor a atributy, které se pošlou s uploadem. */
interface QueuedAttachment {
  file: File;
  name: string;
  description: string;
  categoryId: number | null;
}

/** Dnešek v lokálním čase - toISOString() by kolem půlnoci vracel jiný den (UTC). */
const today = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

export default function MaintenanceCostModal({
  isVisible,
  onClose,
  onSaved,
  assetId,
  costTypes,
  data,
  currency,
  isReadOnly,
}: Props) {
  const { translate } = useTranslation();
  const transFormName = 'Asset.MaintenanceCost';
  const formRef = useRef<Form>(null);

  const transFileFormName = 'File';
  const [costId, setCostId] = useState<number>(data?.id ?? 0);
  const [saving, setSaving] = useState<boolean>(false);
  // fronta příloh vybraných při zakládání - nahraje se až po založení nákladu
  const [files, setFiles] = useState<QueuedAttachment[]>([]);
  const [formData] = useState<MaintenanceCostInputs>(() => ({
    id: data?.id ?? 0,
    assetId,
    typeId: data?.typeId ?? null,
    supplier: data?.supplier ?? '',
    accountingDate: data?.accountingDate ?? today(),
    description: data?.description ?? '',
    amount: data?.amount ?? 0,
    reference: data?.reference ?? '',
    // viditelnost se z formuláře needituje, na API jde vždy true
    visible: true,
  }));

  // kategorie příloh - API vrací jen ty, na které má uživatel roli
  const { data: attachmentCategories } = useSWR<AttachmentCategory[]>(
    costId > 0 ? null : `${API_URL.ATTACHMENT_CATEGORY_LIST_BY_ENTITY}?entity=MaintenanceCost`,
    fetcher,
  );
  const attachmentCategoryItems = useMemo(() => attachmentCategories ?? [], [attachmentCategories]);

  const handleFilesChanged = (e: any) => {
    const selected: File[] = e.value ?? [];
    const added = selected
      .filter((f) => {
        if (f.size <= MAX_FILE_SIZE) return true;
        notify(`${f.name} - ${translate!('File is too large', '')}`, 'error');
        return false;
      })
      // název se předvyplní názvem souboru, stejně jako to dělá BE při prázdném názvu
      .map((f) => ({ file: f, name: f.name, description: '', categoryId: null }));
    if (added.length) setFiles([...files, ...added]);
  };

  const handleFileChanged = (index: number, patch: Partial<QueuedAttachment>) =>
    setFiles(files.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const handleFileRemoved = (index: number) => setFiles(files.filter((_, i) => i !== index));

  /** Nahraje frontu příloh k založenému nákladu - jeden požadavek na soubor. */
  const uploadQueuedFiles = async (entityId: number) => {
    for (const item of files) {
      await uploadAttachment('MaintenanceCost', entityId, item.file, {
        name: item.name,
        description: item.description,
        categoryId: item.categoryId,
      });
    }
  };

  // druhy nákladu - u editace doplníme i skrytý druh, ať o něj záznam nepřijde
  const costTypeItems = (() => {
    const items = costTypes ? [...costTypes] : [];
    if (data?.typeId && !items.some((t) => t.id === data.typeId))
      items.push({ id: data.typeId, name: data.typeName, sortOrder: 0, visible: false });
    return items;
  })();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!formRef.current?.instance.validate().isValid) return;

    setSaving(true);
    if (costId > 0) {
      await putApi({
        url: API_URL.MAINTENANCE_COST_PUT,
        data: { ...formData, id: costId, assetId },
        callAfterSuccess: () => {
          onSaved();
          onClose();
        },
      });
    } else {
      // postApi na callAfterSuccess nečeká, proto se id bere z návratové hodnoty -
      // jinak by se tlačítka odemkla dřív, než doběhne nahrávání příloh
      const created = await postApi<MaintenanceCost>({
        url: API_URL.MAINTENANCE_COST_POST,
        data: { ...formData, assetId },
      });
      if (created?.id) {
        // id z odpovědi je potřeba pro upload příloh (Attachment/Upload?entityId=...)
        await uploadQueuedFiles(created.id);
        setFiles([]);
        setCostId(created.id);
        onSaved();
      }
    }
    setSaving(false);
  };

  return (
    <Popup
      visible={isVisible}
      dragEnabled={false}
      closeOnOutsideClick={false}
      onHiding={onClose}
      showTitle
      title={translate!(
        costId > 0 ? 'Edit maintenance cost' : 'New maintenance cost',
        transFormName,
      )}
      width={'80%'}
      height={'auto'}
    >
      <form onSubmit={handleSubmit}>
        <Form
          formData={formData}
          showColonAfterLabel
          showValidationSummary
          ref={formRef}
          readOnly={isReadOnly}
          colCount={2}
        >
          <GroupItem>
            <SimpleItem
              dataField="accountingDate"
              editorType="dxDateBox"
              editorOptions={{ ...dateFieldOptionsWithSerialization, width: 300 }}
            >
              <Label text={translate!('Accounting date', transFormName)} />
              <RequiredRule message={translate!('Accounting date is required', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="typeId"
              editorType="dxSelectBox"
              editorOptions={{
                items: costTypeItems,
                valueExpr: 'id',
                displayExpr: 'name',
                width: 300,
              }}
            >
              <Label text={translate!('Cost type', transFormName)} />
              <RequiredRule message={translate!('Cost type is required', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="supplier"
              editorType="dxTextBox"
              editorOptions={{ width: 300, maxLength: 200 }}
            >
              <Label text={translate!('Supplier', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="amount"
              editorType="dxNumberBox"
              editorOptions={{
                width: 300,
                format: { type: 'fixedPoint', precision: 2 },
                min: 0,
                showSpinButtons: false,
              }}
            >
              <Label
                text={`${translate!('Amount', transFormName)}${currency ? ` (${currency})` : ''}`}
              />
              <RequiredRule message={translate!('Amount is required', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="reference"
              editorType="dxTextBox"
              editorOptions={{ width: 300, maxLength: 100 }}
            >
              <Label text={translate!('Reference', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <EmptyItem />
          <GroupItem colSpan={2}>
            <SimpleItem
              dataField="description"
              editorType="dxTextArea"
              editorOptions={{ height: 80, maxLength: 500 }}
            >
              <Label text={translate!('Description', transFormName)} />
            </SimpleItem>
          </GroupItem>

          {/* přílohy jen při zakládání - po uložení je nahradí plnohodnotný grid příloh */}
          <GroupItem colSpan={2} visible={costId === 0 && !isReadOnly}>
            <SimpleItem>
              <Label text={translate!('File upload', transFileFormName)} />
              {/* useButtons, ne useForm - v useForm si drop obsluhuje nativní input[type=file],
                  který přijme jen soubor z disku. V useButtons ho odbaví DevExtreme v JS přes
                  dataTransfer.files, takže projde i obrázek přetažený ze stránky v prohlížeči.
                  Vlastní upload se nespouští, soubory odejdou až s nákladem. */}
              <div className="maintenance-cost-file-upload">
                <FileUploader
                  name={'files'}
                  multiple
                  accept={'*'}
                  uploadMode={'useButtons'}
                  selectButtonText={translate!('Select files', transFileFormName)}
                  labelText={translate!('or drop files here', transFileFormName)}
                  onValueChanged={handleFilesChanged}
                  showFileList={false}
                  value={[]}
                  maxFileSize={MAX_FILE_SIZE}
                />
              </div>
              <div className="maintenance-cost-files">
                {files.map((item, i) => (
                  <div className="file" key={`${item.file.name}_${i}`}>
                    <div className="head">
                      <span className="file-name">{item.file.name}</span>
                      <Button
                        icon="close"
                        stylingMode="text"
                        hint={translate!('Cancel', '')}
                        onClick={() => handleFileRemoved(i)}
                      />
                    </div>
                    <div className="field">
                      <span className="label">{translate!('Name', transFileFormName)}:</span>
                      <TextBox
                        value={item.name}
                        onValueChanged={(e: any) => handleFileChanged(i, { name: e.value })}
                      />
                    </div>
                    <div className="field">
                      <span className="label">{translate!('Description', transFileFormName)}:</span>
                      <TextBox
                        value={item.description}
                        onValueChanged={(e: any) => handleFileChanged(i, { description: e.value })}
                      />
                    </div>
                    <div className="field">
                      <span className="label">{translate!('Category', transFileFormName)}:</span>
                      <SelectBox
                        items={attachmentCategoryItems}
                        valueExpr={'id'}
                        displayExpr={'name'}
                        showClearButton
                        value={item.categoryId}
                        onValueChanged={(e: any) =>
                          handleFileChanged(i, { categoryId: e.value ?? null })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </SimpleItem>
          </GroupItem>

          <GroupItem colSpan={2} colCount={6}>
            <ButtonItem>
              <ButtonOptions
                width={'100%'}
                type={'normal'}
                text={translate!('Close', '')}
                onClick={onClose}
              />
            </ButtonItem>
            <ButtonItem>
              <ButtonOptions
                width={'100%'}
                type={'success'}
                text={translate!('Save', '')}
                disabled={saving || isReadOnly}
                useSubmitBehavior
              />
            </ButtonItem>
            <GroupItem colSpan={4}></GroupItem>
          </GroupItem>
        </Form>
      </form>

      {/* přílohy nákladu - dostupné až po prvním uložení, kdy existuje id */}
      {costId > 0 && (
        <div style={{ marginTop: 20 }}>
          <TabFilesDatagrid id={costId} entityName="MaintenanceCost" isReadonly={!!isReadOnly} />
        </div>
      )}
    </Popup>
  );
}
